import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  Fade,
  Grow,
  IconButton,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HomeIcon from '@mui/icons-material/Home';
import WorkIcon from '@mui/icons-material/Work';
import PlaceIcon from '@mui/icons-material/Place';
import apiClient from '../../services/apiClient';
import moment from 'moment';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
interface AddressDetails {
  city: string;
  road: string;
  state: string;
  suburb: string;
  country: string;
  postcode: string;
}

interface AdditionalInfo {
  type: string;
  timestamp: string;
  display_address: string;
  address_details: AddressDetails;
}

export interface UserLocation {
  id: number;
  user_id: number;
  type: string;
  latitude: number;
  longitude: number;
  additional_info: AdditionalInfo;
  is_confirmed: number;
}

export interface ApiResponse {
  code: number;
  message: string;
  data: {
    user_locations: UserLocation[];
  };
}

const UserLocations: React.FC<{ userId: number, onEdit?: (location: UserLocation) => void; }> = ({ userId, onEdit }) => {
  const [locations, setLocations] = useState<UserLocation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        const response = await apiClient<ApiResponse>(
          `v1/resource/UserLocation?user_id=${userId}`
        );
        setLocations(response.data.user_locations);
      } catch (err: any) {
        setError(err?.message || 'Failed to fetch locations');
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, [userId]);

  const getLocationIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'home':
        return <HomeIcon sx={{ fontSize: 28 }} />;
      case 'work':
        return <WorkIcon sx={{ fontSize: 28 }} />;
      default:
        return <PlaceIcon sx={{ fontSize: 28 }} />;
    }
  };
  const handleDelete = async (id: number) => {
    // Show confirmation alert
    const confirmDelete = window.confirm("Are you sure you want to delete this location?");
    if (!confirmDelete) return; // Exit if user cancels

    try {
      await apiClient(`/v1/resource/UserLocation/${id}`, { method: 'DELETE' });
      setLocations((prev) => prev.filter((loc) => loc.id !== id));
      alert("Location deleted successfully!");
    } catch (err: any) {
      alert(err.message || 'Failed to delete location');
    }
  };






  const handleEdit = (location: UserLocation) => {
    // You can open a modal or pass location to AddLocationForm in edit mode
    alert(`Edit location: ${location.id}`);
  };
  const getLocationGradient = (type: string) => {
    switch (type.toLowerCase()) {
      case 'home':
        return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'work':
        return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
      default:
        return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="200px"
        gap={2}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'inline-flex',
          }}
        >
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: '#667eea',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LocationOnIcon sx={{ color: '#667eea', fontSize: 28 }} />
          </Box>
        </Box>
        <Typography
          variant="body1"
          sx={{
            color: '#667eea',
            fontWeight: 600,
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.5 },
            },
          }}
        >
          Loading locations...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Fade in>
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 4,
            background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            border: '2px solid rgba(245, 87, 108, 0.2)',
          }}
        >
          <Typography
            color="error"
            variant="h6"
            sx={{ fontWeight: 700 }}
          >
            ⚠️ {error}
          </Typography>
        </Box>
      </Fade>
    );
  }

  if (locations.length === 0) {
    return (
      <Fade in>
        <Box
          sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: 4,
            background: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
            border: '2px dashed rgba(102, 126, 234, 0.3)',
          }}
        >
          <LocationOnIcon sx={{ fontSize: 64, color: '#667eea', opacity: 0.6, mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 600 }}>
            No locations found
          </Typography>
        </Box>
      </Fade>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      {locations.map((loc, index) => (
        <Grow
          in
          key={loc.id}
          timeout={400 + index * 150}
          style={{ transformOrigin: '0 0 0' }}
        >
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 4,
              border: '2px solid transparent',
              background: 'linear-gradient(white, white) padding-box, ' +
                getLocationGradient(loc.type) + ' border-box',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: '0 12px 40px rgba(102, 126, 234, 0.25)',
                '& .location-icon-wrapper': {
                  transform: 'scale(1.1) rotate(5deg)',
                },
                '& .gradient-overlay': {
                  opacity: 0.08,
                },
              },
            }}
          >
            {/* Gradient Overlay */}
            <Box
              className="gradient-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: getLocationGradient(loc.type),
                opacity: 0.05,
                transition: 'opacity 0.4s ease',
                pointerEvents: 'none',
              }}
            />

            {/* Animated Background Orb */}
            <Box
              sx={{
                position: 'absolute',
                top: '-30%',
                right: '-15%',
                width: '200px',
                height: '200px',
                background: getLocationGradient(loc.type),
                opacity: 0.1,
                borderRadius: '50%',
                filter: 'blur(40px)',
                animation: 'float 6s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0px)' },
                  '50%': { transform: 'translateY(-20px)' },
                },
              }}
            />

            <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              {/* Header Section */}
              <Box display="flex" alignItems="center" gap={2} mb={2.5}>
                <Box
                  className="location-icon-wrapper"
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    background: getLocationGradient(loc.type),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                    transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: -4,
                      borderRadius: 3,
                      background: getLocationGradient(loc.type),
                      opacity: 0.3,
                      filter: 'blur(8px)',
                    },
                  }}
                >
                  {getLocationIcon(loc.type)}
                </Box>

                <Box flex={1}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        background: getLocationGradient(loc.type),
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.5px',
                      }}
                    >
                      {loc.type.toUpperCase()}
                    </Typography>
                    {loc.is_confirmed ? (
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                        label="Confirmed"
                        size="small"
                        sx={{
                          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          border: 'none',
                          boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
                          '& .MuiChip-icon': { color: 'white' },
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                        label="Pending"
                        size="small"
                        sx={{
                          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          border: 'none',
                          boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)',
                          '& .MuiChip-icon': { color: 'white' },
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Box display="flex" gap={1}>
                  <IconButton color="primary" onClick={() => onEdit?.(loc)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDelete(loc.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Address Section */}
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  backgroundColor: 'rgba(102, 126, 234, 0.05)',
                  border: '1px solid rgba(102, 126, 234, 0.1)',
                  mb: 2,
                }}
              >
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  <LocationOnIcon
                    sx={{
                      color: '#667eea',
                      fontSize: 22,
                      mt: 0.3,
                      animation: 'bounce 2s ease-in-out infinite',
                      '@keyframes bounce': {
                        '0%, 100%': { transform: 'translateY(0)' },
                        '50%': { transform: 'translateY(-5px)' },
                      },
                    }}
                  />
                  <Box flex={1}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#667eea',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        mb: 0.5,
                      }}
                    >
                      Address
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: '#333',
                        lineHeight: 1.6,
                      }}
                    >
                      {loc.additional_info.display_address}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Details Grid */}
              <Box
                display="grid"
                gridTemplateColumns="repeat(2, 1fr)"
                gap={2}
              >
                {/* Coordinates */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
                    border: '1px solid rgba(102, 126, 234, 0.15)',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#667eea',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    Latitude
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#333',
                      fontFamily: 'monospace',
                    }}
                  >
                    {loc.latitude.toFixed(6)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(240, 147, 251, 0.08) 0%, rgba(245, 87, 108, 0.08) 100%)',
                    border: '1px solid rgba(240, 147, 251, 0.15)',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#f093fb',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    Longitude
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#333',
                      fontFamily: 'monospace',
                    }}
                  >
                    {loc.longitude.toFixed(6)}
                  </Typography>
                </Box>
              </Box>

              {/* Timestamp */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 3,
                  background: 'linear-gradient(90deg, rgba(102, 126, 234, 0.05) 0%, rgba(240, 147, 251, 0.05) 100%)',
                  border: '1px solid rgba(102, 126, 234, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <AccessTimeIcon sx={{ color: '#667eea', fontSize: 20 }} />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#667eea',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                    }}
                  >
                    Timestamp
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#333',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                    }}
                  >
                    {moment(loc.additional_info.timestamp).format('DD-MM-YYYY hh:mm A')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grow>
      ))}
    </Box>
  );
};

export default UserLocations;